/**
 * ParentGuard
 *
 * Protects routes that require the user to have linked children.
 * Parent is a person-level relationship, not a context.
 *
 * Must be used inside AuthGuard (assumes user is already authenticated).
 */

import { Navigate, Link as RouterLink } from 'react-router-dom'
import { FiUsers } from 'react-icons/fi'
import { useAuthStore } from '../../store'
import { hasLinkedChildren } from '../../lib/contextHelpers'

interface ParentGuardProps {
  children: React.ReactNode
  redirectTo?: string
  fallback?: React.ReactNode
}

function NotAParentFallback() {
  return (
    <div className="p-8 max-w-md mx-auto mt-20 text-center bg-background rounded-lg border border-border">
      <div className="flex flex-col items-center gap-4">
        <FiUsers className="h-12 w-12 text-muted-foreground" />
        <p className="text-xl font-semibold text-foreground">
          Parent Access Required
        </p>
        <p className="text-muted-foreground">
          This page is available to parents with linked child accounts.
        </p>
        <RouterLink to="/">
          <button className="bg-primary text-primary-foreground hover:bg-primary/90 px-4 py-2 rounded-md font-medium">
            Go Back Home
          </button>
        </RouterLink>
      </div>
    </div>
  )
}

export function ParentGuard({
  children,
  redirectTo,
  fallback,
}: ParentGuardProps) {
  const user = useAuthStore((s) => s.user)

  if (!user || !hasLinkedChildren(user)) {
    if (redirectTo) return <Navigate to={redirectTo} replace />
    return <>{fallback ?? <NotAParentFallback />}</>
  }

  return <>{children}</>
}
